import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus, Users } from "lucide-react"

import { DataState } from "@/components/DataState"
import { ReasonDialog } from "@/components/ReasonDialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { useI18n, t } from "@/lib/i18n"
import type { Client } from "@/lib/types"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { FieldsDialog, type FieldSpec, type FieldValue } from "@/routes/masters/FieldsDialog"
import { ContactosDialog } from "@/routes/masters/ContactosDialog"
import { fetchClients, fetchContracts, saveClient } from "@/routes/masters/api"
import { fetchLineasDeLaHoja } from "@/routes/finance/api"

/** Valor del desplegable para «sin línea»: Radix no admite `""` como opción. */
const SIN_LINEA = "__sin_linea__"

/**
 * LA LÍNEA DE LA HOJA, como campo del cliente. Cesar, 2026-10-05: «debería de
 * aparecer una lista con cada cliente con su monto en cada mes de venta». La
 * hoja no guarda clientes debajo de «B2B Lead Gen»; el cliente dice en qué
 * línea cae y el desglose de Finanzas la abre por él. Las opciones son las
 * líneas de ingreso de la hoja, leídas del backend: una lista fija diría
 * líneas que esta empresa no tiene.
 */
function campoDeLinea(lineas: string[] | undefined, ayuda: string): FieldSpec {
  return {
    type: "select",
    name: "lineaHoja",
    label: "Línea de la hoja",
    wide: true,
    help: ayuda,
    options: [{ value: SIN_LINEA, label: "Sin línea (no se abre en el desglose)" },
              ...(lineas ?? []).map((l) => ({ value: l, label: l }))],
  }
}

/*
  LO QUE SE QUITÓ DE ESTA PANTALLA, Y POR QUÉ. Tenía «DSO real», «Brecha»,
  «Estado» y «Política de cobranza». Las cuatro columnas se veían bien y
  ninguna decía nada: el DSO y la brecha eran SIEMPRE 0 (no hay endpoint de
  aging), el estado era SIEMPRE «activo» (`party` no tiene estado) y la política
  elegida en el formulario no se guardaba en ningún sitio (la FK va al revés).
  Una columna que enseña siempre lo mismo no es información, es ruido con
  aspecto de dato — y «brecha 0» se lee como «todos pagan a tiempo».
*/
const FIELDS: FieldSpec[] = [
  { type: "text", name: "name", label: "Razon social", required: true, wide: true },
  { type: "text", name: "ruc", label: "RUC / Tax ID", required: true },
  {
    type: "select",
    name: "country",
    label: "Pais",
    required: true,
    options: [
      { value: "PE", label: "Peru" },
      { value: "CO", label: "Colombia" },
      { value: "CL", label: "Chile" },
      { value: "MX", label: "Mexico" },
      { value: "US", label: "Estados Unidos" },
      { value: "ES", label: "Espana" },
    ],
  },
  {
    type: "select",
    name: "currency",
    label: "Moneda",
    required: true,
    options: [
      { value: "PEN", label: "PEN - Soles" },
      { value: "USD", label: "USD - Dolares" },
      { value: "EUR", label: "EUR - Euros" },
    ],
  },
  {
    type: "number",
    name: "paymentTermsDays",
    label: "Termino de pago pactado (dias)",
    required: true,
    help: "Lo que se pactó con el cliente. La cobranza avisa a partir de esta fecha.",
  },
  {
    type: "number",
    name: "diaDePago",
    label: "Dia del mes en que suele pagar",
    help: "El día de su corrida de pagos (p. ej. 15 o 30). Sirve para saber cuándo esperar la plata.",
  },
  {
    type: "text",
    name: "enlaceContrato",
    label: "Enlace al contrato firmado",
    placeholder: "https://drive.google.com/…",
    wide: true,
    help: "Drive, DocuSign o donde esté. Las fechas de inicio, fin y renovación van en Contratos, que es de donde sale el forecast.",
  },
  {
    /*
      EL CORREO AL QUE SE LE RECLAMA, y vive AQUÍ.

      Cesar, sobre cobranzas: «eliminar esa línea de la parte de cobranza y que
      sólo salga el botón para darle click a enviar». La línea era un campo de
      texto POR FILA de la cartera pidiendo la dirección, así que el dato no se
      guardaba en ninguna parte y cada persona tecleaba uno distinto cada mes.

      Es del cliente, se pone una vez y la cartera lo usa. Ver migración 0024.
    */
    type: "text",
    name: "collectionEmail",
    label: "Correo para los avisos de cobranza",
    placeholder: "cuentasporpagar@cliente.com",
    wide: true,
    help: "A dónde se le reclama. Suele ser un buzón de cuentas por pagar y no una persona: el recordatorio al gerente con el que se firmó tensa la relación sin que nadie pague antes. Sin esto, la cartera no puede mandarle el aviso.",
  },
]

export function ClientsPage() {
  const { can } = useRbac()
  const { term } = useI18n()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<Client | null>(null)
  const [creating, setCreating] = React.useState(false)
  /*
    ACTIVAR Y DESACTIVAR (migración 0033). Cesar: «es importante que sí se pueda
    activar y desactivar clientes y proveedores». Desactivar no borra: la ficha
    y su historial siguen; lo que cambia es que deja de ofrecerse para lo nuevo.
    Los inactivos se esconden por defecto y se enseñan con la casilla, para que
    la lista del día a día sea la de los que operan.
  */
  const [cambiandoEstado, setCambiandoEstado] = React.useState<Client | null>(null)
  const [mostrarInactivos, setMostrarInactivos] = React.useState(false)
  const [contactosDe, setContactosDe] = React.useState<Client | null>(null)

  const clientsQuery = useQuery({ queryKey: ["clients"], queryFn: fetchClients })
  const lineas = useQuery({ queryKey: ["lineas-hoja", "Revenue"], queryFn: () => fetchLineasDeLaHoja("Revenue") })
  const fields: FieldSpec[] = [
    ...FIELDS,
    campoDeLinea(lineas.data?.lineas, "En qué línea de ingreso de tu hoja vende. Con esto Finanzas › Desglose abre la línea por cliente."),
  ]
  // Los contratos, para decir desde cuándo y hasta cuándo sin salir de aquí
  // (Cesar: «la fecha de inicio y la fecha de renovación o de fin del contrato»).
  const contractsQuery = useQuery({ queryKey: ["contracts"], queryFn: fetchContracts })
  const contratosDe = (clientId: string) =>
    (contractsQuery.data ?? []).filter((c) => c.clientId === clientId && c.status !== "vencido")

  const mutation = useMutation({
    mutationFn: (payload: { values: Record<string, FieldValue>; reason: string; id?: string }) =>
      saveClient({
        data: {
          ...(payload.values as Partial<Client>),
          lineaHoja: payload.values.lineaHoja === SIN_LINEA ? "" : String(payload.values.lineaHoja ?? ""),
          id: payload.id,
        },
        reason: payload.reason,
      }),
    onSuccess: () => {
      toast({ title: "Cliente guardado", description: "El cambio quedo versionado con motivo." })
      setEditing(null)
      setCreating(false)
      setCambiandoEstado(null)
      void queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
    onError: onWriteError("el cliente"),
  })

  const todos = clientsQuery.data ?? []
  const inactivos = todos.filter((c) => !c.activo).length
  const clients = mostrarInactivos ? todos : todos.filter((c) => c.activo)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="max-w-[80ch] text-xs leading-[1.15rem] text-muted-foreground">
          {t("Quién te compra, con qué plazo pactado y a qué correo se le reclama. Sin correo, la cobranza no le puede escribir.")}</p>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <Checkbox
              checked={mostrarInactivos}
              onCheckedChange={(v) => setMostrarInactivos(v === true)}
            />
            {t("Mostrar inactivos")}{inactivos ? ` (${inactivos})` : ""}
          </label>
          <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
            <Plus />
            {t("Nuevo cliente")}</Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-panel border bg-surface shadow-xs">
        <DataState
          loading={clientsQuery.isLoading}
          error={clientsQuery.error}
          onRetry={() => void clientsQuery.refetch()}
          empty={clients.length === 0}
          emptyMessage={t("Todavia no hay clientes en el maestro.")}
          emptyHint={t("Sin clientes no hay margen bruto por cliente ni proyeccion de cobranza: son la contraparte de todo lo que factura la empresa.")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cliente")}</TableHead>
                <TableHead className="w-32">{term.identificador_fiscal}</TableHead>
                <TableHead className="w-16">{t("Pais")}</TableHead>
                <TableHead className="w-20">{t("Moneda")}</TableHead>
                <TableHead className="w-28 text-right">{t("Pactado (d)")}</TableHead>
                <TableHead className="w-20 text-right">{t("Paga el")}</TableHead>
                <TableHead className="w-44">{t("Contratos")}</TableHead>
                {/* A quién se le puede reclamar y a quién no, de un vistazo. */}
                <TableHead className="w-52">{t("Correo de cobranza")}</TableHead>
                <TableHead className="w-20">{t("Estado")}</TableHead>
                <TableHead className="w-56" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => {
                return (
                  <TableRow key={client.id} className={cn(!client.activo && "text-muted-foreground")}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell className="code">{client.ruc}</TableCell>
                    <TableCell>{client.country}</TableCell>
                    <TableCell>{client.currency}</TableCell>
                    <TableCell className="num">{client.paymentTermsDays}</TableCell>
                    <TableCell className="num text-muted-foreground">{client.diaDePago ?? "—"}</TableCell>
                    <TableCell className="text-2xs">
                      {(() => {
                        const cs = contratosDe(client.id)
                        if (cs.length === 0) return <span className="text-muted-foreground">—</span>
                        const fin = cs.map((c) => c.endDate).filter(Boolean).sort().slice(-1)[0]
                        return (
                          <span title={cs.map((c) => `${c.code}: ${c.startDate} → ${c.endDate || "sin fin"}`).join("\n")}>
                            {cs.length} {t("vigente(s)")}{fin ? ` · ${t("hasta")} ${fin}` : ""}
                          </span>
                        )
                      })()}
                      {client.enlaceContrato ? (
                        <a href={client.enlaceContrato} target="_blank" rel="noreferrer"
                           className="ml-1 text-brand underline-offset-2 hover:underline">{t("ver")}</a>
                      ) : null}
                    </TableCell>
                    {/*
                      SIN CORREO SE MARCA EN ÁMBAR, no se deja en blanco. Un
                      hueco vacío se lee como «todavía no lo he mirado»; esto es
                      «a este cliente NO se le puede reclamar», que es una
                      consecuencia y hay que verla desde aquí.
                    */}
                    <TableCell className="truncate text-2xs" title={client.collectionEmail}>
                      {client.collectionEmail || (
                        <span className="text-warning-foreground">{t("sin correo")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={client.activo ? "success-soft" : "outline"} estado>
                        {client.activo ? t("activo") : t("inactivo")}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button size="xs" variant="ghost" onClick={() => setContactosDe(client)}
                              title={t("Contactos")}>
                        <Users />
                        {client.contactos || ""}
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!can("edit")}
                        onClick={() => setCambiandoEstado(client)}
                      >
                        {client.activo ? t("Desactivar") : t("Activar")}</Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!can("edit")}
                        onClick={() => setEditing(client)}
                      >
                        <Pencil />
                        {t("Editar")}</Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </div>

      {contactosDe ? (
        <ContactosDialog
          open
          onOpenChange={(v) => { if (!v) setContactosDe(null) }}
          partyId={contactosDe.id}
          nombre={contactosDe.name}
          puedeEditar={can("edit")}
        />
      ) : null}

      {cambiandoEstado ? (
        <ReasonDialog
          open
          onOpenChange={(v) => { if (!v) setCambiandoEstado(null) }}
          title={cambiandoEstado.activo
            ? `${t("Desactivar")} · ${cambiandoEstado.name}`
            : `${t("Activar")} · ${cambiandoEstado.name}`}
          description={cambiandoEstado.activo
            ? t("No se borra nada: conserva sus facturas y su historial de cobranza. Deja de ofrecerse para contratos nuevos.")
            : t("Vuelve a ofrecerse para contratos nuevos.")}
          confirmLabel={cambiandoEstado.activo ? t("Desactivar") : t("Activar")}
          variant={cambiandoEstado.activo ? "destructive" : "default"}
          suggestions={cambiandoEstado.activo
            ? ["Dejó de operar con nosotros.", "Cliente duplicado: se usa la otra ficha."]
            : ["Volvió a contratar."]}
          pending={mutation.isPending}
          onConfirm={(reason) =>
            mutation.mutateAsync({
              values: { activo: !cambiandoEstado.activo }, reason, id: cambiandoEstado.id,
            }).then(() => undefined)}
        />
      ) : null}

      <FieldsDialog
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        title={editing ? `Editar cliente · ${editing.name}` : "Nuevo cliente"}
        description={t("El plazo pactado y el correo de cobranza alimentan directamente los avisos y la proyección de caja.")}
        fields={fields}
        initial={
          editing
            ? {
                name: editing.name,
                ruc: editing.ruc,
                country: editing.country,
                currency: editing.currency,
                paymentTermsDays: editing.paymentTermsDays,
                collectionEmail: editing.collectionEmail,
                diaDePago: editing.diaDePago ?? undefined,
                enlaceContrato: editing.enlaceContrato,
                lineaHoja: editing.lineaHoja || SIN_LINEA,
              }
            : { country: "PE", currency: "PEN", paymentTermsDays: 30, lineaHoja: SIN_LINEA }
        }
        pending={mutation.isPending}
        reasonSuggestions={[
          "Renegociacion de terminos con el cliente.",
          "Alta de cliente nuevo aprobada por gerencia.",
        ]}
        onSubmit={(values, reason) =>
          mutation.mutateAsync({ values, reason, id: editing?.id }).then(() => undefined)
        }
      />
    </div>
  )
}
